import ExpandMore from "@mui/icons-material/ExpandMore";
import { Dispatch, SetStateAction, useContext, useState } from "react";
import { createContext } from "react";
import { WorkflowActivitiesRow } from "./types";
import { Button, Divider, Menu, MenuList, useTheme } from "@mui/material";
import { AssignMenuItem, BulkAssignDialog, UnassignMenuItem } from "./Assign";
import { SetPortalToManual, SetPortalToAuto } from "./Portal";
import { RequeueMenuItem } from "./Requeue";
import {
  ManageWorkflowTags,
  ManageWorkflowTagsDialog,
} from "./ManageWorkflowTags";

const buttonId = "workflow-activities-bulk-actions-button";
const menuId = "workflow-activities-bulk-actions-menu";

type DialogState =
  | "assign"
  | "manageTags"
  | "manageWorkflowTags"
  | "verify&submit"
  | "resetReports"
  | null;

const bulkActionContext = createContext<{
  selectedRows: WorkflowActivitiesRow[];
  dialogState: DialogState;
  setDialogState: Dispatch<SetStateAction<DialogState>>;
  onClose: () => void;
}>({
  selectedRows: [],
  dialogState: null,
  setDialogState: () => {},
  onClose: () => {},
});

export function useBulkActionContext() {
  return useContext(bulkActionContext);
}

export function BulkActionsMenu({
  selectedRows,
}: {
  selectedRows: WorkflowActivitiesRow[];
}) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [dialogState, setDialogState] = useState<DialogState>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const onClose = () => {
    setAnchorEl(null);
    setDialogState(null);
  };

  const theme = useTheme();

  return (
    <bulkActionContext.Provider
      value={{ selectedRows, onClose, dialogState, setDialogState }}
    >
      <Button
        id={buttonId}
        size="medium"
        aria-controls={open ? menuId : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        disabled={!selectedRows.length}
        variant="contained"
        onClick={handleClick}
        endIcon={<ExpandMore />}
        sx={{
          whiteSpace: "nowrap",
          backgroundColor: theme.palette.secondary.main,
        }}
      >
        Bulk Actions
      </Button>
      <Menu
        id={menuId}
        anchorEl={anchorEl}
        open={open}
        onClose={onClose}
        MenuListProps={{ "aria-labelledby": buttonId }}
      >
        <MenuList>
          <AssignMenuItem />
          <UnassignMenuItem />
          <Divider />
          <SetPortalToManual />
          <SetPortalToAuto />
          <Divider />
          <RequeueMenuItem />
          <Divider />
          <ManageWorkflowTags />
        </MenuList>
      </Menu>
      <BulkAssignDialog />
      <ManageWorkflowTagsDialog />
    </bulkActionContext.Provider>
  );
}
